import { Toman, TomanPrice } from '@/common/components/atom/toman/toman';
import { useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import ChevronIcon from '@/common/components/icons/chevron';

/** Fired by the mobile pay bar's payable amount to reveal and scroll to the details. */
export const OPEN_INVOICE_EVENT = 'factor:open-invoice';
import Skeleton from '@/common/components/atom/skeleton';
import Text from '@/common/components/atom/text';
import { addCommas } from '@persian-tools/persian-tools';
import { ExtraWalletTopupEditor, openExtraWalletTopupAmount } from '../extraWalletTopup';

interface InvoiceProps {
  serviceFeeText?: string;
  serviceFee?: string;
  price?: string;
  priceText: string;
  tax?: string;
  discount?: string;
  /** Paid by someone else (e.g. the patient's organization), shown like the discount row. Already left out of `totalPrice`. */
  creditRows?: { label: string; amountRial: number }[];
  walletAmount?: number;
  totalPrice?: string;
  /** Extra wallet top-up in rial — when set, payable = invoice + extra (no partial-wallet subtract). */
  extraWalletTopupRial?: number;
  showExtraWalletTopup?: boolean;
  extraWalletTopupEnabled?: boolean;
  extraWalletTopupToman?: number;
  onExtraWalletTopupEnabledChange?: (enabled: boolean) => void;
  onExtraWalletTopupTomanChange?: (toman: number) => void;
  loading: boolean;
  payment_description_html?: string;
  onPayableAmountChange?: (amount?: number) => void;
}
export const Invoice = (props: InvoiceProps) => {
  const {
    serviceFeeText,
    serviceFee,
    price,
    priceText,
    walletAmount,
    tax,
    discount,
    creditRows = [],
    totalPrice,
    extraWalletTopupRial = 0,
    showExtraWalletTopup = false,
    extraWalletTopupEnabled = false,
    extraWalletTopupToman = 0,
    onExtraWalletTopupEnabledChange,
    onExtraWalletTopupTomanChange,
    loading = false,
    payment_description_html,
    onPayableAmountChange,
  } = props;
  const rialFromField = (value?: string) => {
    if (value == null || value === '') return NaN;
    const trimmed = String(value).trim().toLowerCase();
    if (trimmed === 'null' || trimmed === 'undefined' || trimmed === 'nan') return NaN;
    const n = Number(value);
    return Number.isFinite(n) ? n : NaN;
  };
  const formattedPrice = Number.isFinite(rialFromField(price)) ? addCommas(Math.round(rialFromField(price) / 10)) : null;
  const formattedTax = Number.isFinite(rialFromField(tax)) ? addCommas(Math.round(rialFromField(tax) / 10)) : null;
  // مبلغ قابل پرداخت:
  // - شارژ اضافه: کل فاکتور + extra (مسیر _topups؛ کیف کسر نمی‌شود)
  // - موجودی جزئی (۰ < کیف < مبلغ): ماندهٔ درگاه = فاکتور − کیف (مورد ۲ هنوز انتخاب جایگزین ندارد)
  // - کیف کافی یا صفر: همان مبلغ فاکتور (بعد از تخفیف) — وقتی کافی است صفر نشان نده
  const totalPriceRial = rialFromField(totalPrice);
  const walletRial = typeof walletAmount === 'number' && !Number.isNaN(walletAmount) ? walletAmount : 0;
  const extraRial = Math.max(0, Math.floor(Number(extraWalletTopupRial) || 0));
  // While extra box is open, payable = invoice + extra (live); don't fall back to partial-wallet subtract
  const payableRial = Number.isFinite(totalPriceRial)
    ? extraWalletTopupEnabled || extraRial > 0
      ? Math.max(0, totalPriceRial) + extraRial
      : walletRial > 0 && walletRial < totalPriceRial
      ? Math.max(0, totalPriceRial - walletRial)
      : Math.max(0, totalPriceRial)
    : NaN;
  const formattedTotalPrice = Number.isFinite(payableRial) ? addCommas(Math.round(payableRial / 10)) : null;
  const formattedWalletAmount =
    (walletAmount == 0 || walletAmount) && typeof walletAmount === 'number' ? addCommas(Math.round(+walletAmount / 10)) : null;
  const discountRial = rialFromField(discount);
  const formattedDiscount = Number.isFinite(discountRial) && discountRial > 0 ? addCommas(Math.round(discountRial / 10)) : null;
  const showWalletRow = !!formattedWalletAmount || showExtraWalletTopup;
  const visitTotalToman = Number.isFinite(totalPriceRial) ? Math.round(totalPriceRial / 10) : 0;
  useEffect(() => {
    onPayableAmountChange?.(!loading && Number.isFinite(payableRial) ? payableRial : undefined);
  }, [loading, payableRial, onPayableAmountChange]);

  // Mobile: collapsed by default; desktop always shows the details.
  const [open, setOpen] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);
  useEffect(() => {
    const reveal = () => {
      setOpen(true);
      // Scroll after the expanded content has rendered, so the target position is final.
      setTimeout(() => sectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
    };
    window.addEventListener(OPEN_INVOICE_EVENT, reveal);
    return () => window.removeEventListener(OPEN_INVOICE_EVENT, reveal);
  }, []);

  return (
    <section ref={sectionRef} aria-label="جزئیات پرداخت" className="bg-white scroll-mt-20">
      <h2 className="text-sm font-bold text-slate-900">
        <button
          type="button"
          aria-expanded={open}
          onClick={() => setOpen(value => !value)}
          className="flex w-full items-center justify-between md:pointer-events-none md:cursor-default"
        >
          جزئیات پرداخت
          <ChevronIcon dir={open ? 'top' : 'bottom'} className="md:hidden text-slate-500" />
        </button>
      </h2>
      <div className={clsx('mt-3 md:block', !open && 'hidden')}>
      <div className="flex flex-col gap-2.5 text-xs text-slate-600">
        {loading && [serviceFeeText, priceText, 'مالیات', ...(showWalletRow ? ['موجودی کیف پول'] : [])].filter(Boolean).map((label, index) => (
          <div key={index} className="flex h-5 items-center justify-between gap-4">
            <Skeleton w={index === 0 ? '9rem' : '5rem'} h="14px" rounded="sm" />
            <Skeleton w="5rem" h="14px" rounded="sm" />
          </div>
        ))}
        {!loading && !!serviceFeeText && (
          <div className="flex items-center justify-between gap-4">
            <Text fontWeight="medium" fontSize="xs">
              {serviceFeeText}
            </Text>
            <Text fontWeight="medium" fontSize="xs">
              {serviceFee}
            </Text>
          </div>
        )}

        {!loading && (
          <div className="flex items-center justify-between gap-4">
            <Text fontWeight="medium" fontSize="xs">
              {priceText}
            </Text>
            <Text fontWeight="medium" fontSize="xs">
              <TomanPrice>{formattedPrice}</TomanPrice>
            </Text>
          </div>
        )}

        {!loading && !!formattedTax && Number.isFinite(rialFromField(tax)) && rialFromField(tax) > 0 && (
          <div className="flex items-center justify-between gap-4">
            <Text fontWeight="medium" fontSize="xs">
              مالیات
            </Text>
            <Text fontWeight="medium" fontSize="xs">
              <TomanPrice>{formattedTax}</TomanPrice>
            </Text>
          </div>
        )}
        {!loading && !!formattedDiscount && (
          <div className="flex items-center justify-between gap-4 text-green-700">
            <Text fontWeight="medium" fontSize="xs">
              جمع تخفیف‌ها
            </Text>
            <Text fontWeight="medium" fontSize="xs">
              <TomanPrice>{formattedDiscount}</TomanPrice>
            </Text>
          </div>
        )}

        {!loading &&
          creditRows.map(row => (
            <div key={row.label} className="flex items-center justify-between gap-4 text-green-700">
              <Text fontWeight="medium" fontSize="xs">
                {row.label}
              </Text>
              <Text fontWeight="medium" fontSize="xs">
                <TomanPrice>{addCommas(Math.round(row.amountRial / 10))}</TomanPrice>
              </Text>
            </div>
          ))}

        {!loading && showWalletRow && (
          <div className="flex flex-col gap-2">
            <div className="flex items-center justify-between gap-2">
              <Text fontWeight="medium" fontSize="xs">
                موجودی کیف پول
              </Text>
              <div className="flex items-center gap-1.5">
                <Text fontWeight="medium" fontSize="xs">
                  <TomanPrice>{formattedWalletAmount ?? '۰'}</TomanPrice>
                </Text>
                {showExtraWalletTopup && onExtraWalletTopupEnabledChange && onExtraWalletTopupTomanChange && (
                  <button
                    type="button"
                    aria-label={extraWalletTopupEnabled ? 'بستن شارژ اضافه' : 'افزودن شارژ به کیف پول'}
                    title={extraWalletTopupEnabled ? 'بستن شارژ اضافه' : 'افزودن شارژ به کیف پول'}
                    onClick={() => {
                      if (extraWalletTopupEnabled) {
                        // Keep last amount so reopen restores it
                        onExtraWalletTopupEnabledChange(false);
                        return;
                      }
                      onExtraWalletTopupTomanChange(openExtraWalletTopupAmount(extraWalletTopupToman, visitTotalToman));
                      onExtraWalletTopupEnabledChange(true);
                    }}
                    className="shrink-0 px-0.5 text-lg font-bold leading-none text-primary hover:opacity-80"
                  >
                    {extraWalletTopupEnabled ? '−' : '+'}
                  </button>
                )}
              </div>
            </div>
            {showExtraWalletTopup && onExtraWalletTopupEnabledChange && onExtraWalletTopupTomanChange && (
              <ExtraWalletTopupEditor
                enabled={extraWalletTopupEnabled}
                selectedToman={extraWalletTopupToman}
                onEnabledChange={onExtraWalletTopupEnabledChange}
                onSelectedTomanChange={onExtraWalletTopupTomanChange}
              />
            )}
          </div>
        )}
      </div>

      <div className="mt-2.5 flex items-center justify-between gap-4 border-t border-slate-100 pt-2.5">
        <Text fontWeight="semiBold" fontSize="xs" className="text-slate-900">
          مبلغ قابل پرداخت
        </Text>
        {loading ? (
          <Skeleton w="8rem" h="1.5rem" rounded="full" />
        ) : (
          <span className="inline-flex items-center gap-1 text-slate-900">
            <strong className="text-sm font-semibold tabular-nums">{formattedTotalPrice ?? '—'}</strong>
            <Toman className="text-xs" />
          </span>
        )}
      </div>
      {!loading && payment_description_html && (
        <div className="mt-3 text-sm text-slate-500 [&>p]:m-0" dangerouslySetInnerHTML={{ __html: payment_description_html }} />
      )}
      </div>
    </section>
  );
};
export default Invoice;
